import React, { forwardRef, PropsWithChildren, ReactNode, useState } from 'react';
import { StyleSheet, View, ViewProps } from 'react-native';
import { createPortal, DNABox, DNAIcon, Iffy } from '@alucio/lux-ui/src';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  UniqueIdentifier,
  DragOverlay,
  // CollisionDetections
  rectIntersection,
  closestCenter,
  closestCorners,
} from '@dnd-kit/core';
import {
  useSortable,
  SortableContext,
  sortableKeyboardCoordinates,
  // Strategies
  rectSortingStrategy,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
  rectSwappingStrategy,
} from '@dnd-kit/sortable';
// ModifiersList
import { restrictToVerticalAxis, restrictToWindowEdges } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import { DNABoxProps } from '@alucio/lux-ui/src/components/layout/DNABox/DNABox';

const styles = StyleSheet.create({
  dndIconContainer: {
    padding: 12,
  },
  defaultItemStyle: {
    flexDirection: 'row',
    width: '100%',
    userSelect: 'none',
  },
});

enum StrategiesEnum {
  verticalListSortingStrategy = 'verticalListSortingStrategy',
  rectSortingStrategy = 'rectSortingStrategy',
  horizontalListSortingStrategy = 'horizontalListSortingStrategy',
  rectSwappingStrategy = 'rectSwappingStrategy',
}

type Strategies = keyof typeof StrategiesEnum

const strategies = {
  verticalListSortingStrategy,
  rectSortingStrategy,
  horizontalListSortingStrategy,
  rectSwappingStrategy,
}

export enum ModifiersListEnum {
  restrictToVerticalAxis = 'restrictToVerticalAxis',
  restrictToWindowEdges = 'restrictToWindowEdges',
}

type ModifiersList = keyof typeof ModifiersListEnum

const modifiersList = {
  restrictToVerticalAxis,
  restrictToWindowEdges,
}

enum CollisionDetectionsEnum {
  rectIntersection = 'rectIntersection',
  closestCenter = 'closestCenter',
  closestCorners = 'closestCorners',
}

type CollisionDetections = keyof typeof CollisionDetectionsEnum

const collisionDetections = {
  rectIntersection,
  closestCenter,
  closestCorners,
}

type DNARearrangerWrapperProps = React.FC<PropsWithChildren<DNARearrangerProps>> & {
  SortableItem: React.FC<PropsWithChildren<SortableItemProps>>,
}

interface DNARearrangerProps {
  items: (UniqueIdentifier | { id: UniqueIdentifier })[]
  onDragStart?: (event: DragStartEvent) => void,
  onDragEnd: (event: DragEndEvent) => void,
  collisionDetection?: CollisionDetections,
  modifiers?: ModifiersList[],
  strategy?: Strategies,
  overlay?: ReactNode,
}

type ItemProps = {
  style?: React.CSSProperties,
  children: React.ReactNode,
}

type InteractiveItemProps = {
  id: UniqueIdentifier,
  itemId: UniqueIdentifier,
  containerId: UniqueIdentifier,
  disabled?: boolean,
}

interface SortableItemProps extends InteractiveItemProps {
  style?: React.CSSProperties,
  visible?: boolean,
  handle?: boolean,
  boxContainerStyle?: ViewProps['style'],
  leftIcon?: string,
  iconAlignY?: DNABoxProps['alignY'],
  testID?: string,
}

export const Item = React.memo(
  forwardRef<HTMLImageElement, ItemProps>((props, ref) => {
    const { style, children, ...restProps } = props

    return (
      <div ref={ref} style={style} {...restProps}>
        <DNABox fill>
          {children}
        </DNABox>
      </div>
    );
  }),
);

export const SortableItem: React.FC<PropsWithChildren<SortableItemProps>> = ({
  id,
  itemId,
  containerId,
  visible = true,
  disabled,
  style,
  children,
  handle,
  boxContainerStyle,
  leftIcon = 'dots-six',
  iconAlignY = 'center',
  testID,
}) => {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({
    id,
    disabled,
    data: {
      containerId,
      itemId,
    },
  });

  if (!visible) return null

  const sortableStyle: React.CSSProperties = {
    display: 'flex',
    boxSizing: 'border-box',
    transformOrigin: '0 0',
    // See https://docs.dndkit.com/api-documentation/sensors/touch#recommendations
    touchAction: 'none',
    opacity: isDragging ? 0.3 : undefined,
    transform: CSS.Translate.toString(transform),
    transition,
    userSelect: 'none',
  }

  const finalStyle = StyleSheet.flatten([
    sortableStyle,
    style,
  ]) as React.CSSProperties

  const handleBtnStyle: React.CSSProperties = {
    backgroundColor: 'transparent',
    border: 'none',
  }

  return (
    <Item
      ref={disabled ? undefined : setNodeRef}
      style={finalStyle}
      {...((disabled || handle) ? {} : attributes)}
      {...((disabled || handle) ? {} : listeners)}
    >
      <View style={[styles.defaultItemStyle, boxContainerStyle]} testID={testID}>
        <Iffy is={handle && listeners && attributes}>
          <DNABox alignY={iconAlignY} style={styles.dndIconContainer}>
            <button
              {...listeners}
              {...attributes}
              style={handleBtnStyle}
              data-testID="rearranger-handle"
            >
              <DNAIcon.Styled
                name={leftIcon}
                status="subtle"
                // @ts-ignore
                style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
              />
            </button>
          </DNABox>
        </Iffy>
        {children}
      </View>
    </Item>
  );
}

/**
* The DNARearranger is a drag and drop wrapper that takes the following props:
* * items: an array of objects with a unique identifier id, or just an
*   array of unique identifiers, representing the items to be sorted.
* * onDragEnd: a function that is called when a drag and drop action ends,
*   providing information about the action that just occurred.
* * [collisionDetection](https://docs.dndkit.com/api-documentation/context-provider/collision-detection-algorithms)?: an optional parameter specifying the collision
*   detection strategy to be used for the drag and drop interactions.
* * [modifiers](https://docs.dndkit.com/api-documentation/modifiers)?: an optional array of modifier functions to customize the
*   behavior of the drag and drop interactions.
* * [strategy](https://docs.dndkit.com/presets/sortable/sortable-context#strategy)?: an optional parameter specifying the strategy to be used
*   for the drag and drop interactions.
*
* The DNARearranger.SortableItem is the wrapper for the individual draggable/sortable item
*   that is wrapped inside DNARearranger, this component takes the following props:
* * id: a unique identifier for the item being sorted.
* * itemId: a unique identifier for the item being dragged.
* * containerId: a unique identifier for the container that holds the items being sorted.
* * visible?: an optional boolean value indicating whether the item should be visible.
* * disabled?: an optional boolean value indicating whether the item is disabled.
* * style?: an optional object of CSS styles to be applied to the item.
* * handle?: an optional boolean value indicating whether the item should have a handle for dragging.
*
* @see Checkout [dndkit Storybook](https://master--5fc05e08a4a65d0021ae0bf2.chromatic.com/?path=/story/core-draggable-hooks-usedraggable--basic-setup) for more options.
*/
export const DNARearranger: DNARearrangerWrapperProps = ({
  children,
  items,
  onDragStart,
  onDragEnd,
  collisionDetection,
  modifiers,
  strategy,
  overlay,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(TouchSensor, { activationConstraint: { delay: 50, tolerance: 0 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const chosenModifiers = modifiers?.map(modifierName => modifiersList[modifierName])

  const handleDragStart = (event: DragStartEvent) => {
    setIsDragging(true);
    onDragStart && onDragStart(event)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    setIsDragging(false);
    onDragEnd(event)
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection ? collisionDetections[collisionDetection] : undefined}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      modifiers={chosenModifiers}
    >
      <SortableContext
        items={items}
        strategy={strategy ? strategies[strategy] : undefined}
      >
        {children}
      </SortableContext>
      <Iffy is={overlay}>
        {createPortal(
          <DragOverlay>
            {isDragging ? (
              overlay
            ) : null}
          </DragOverlay>, window.document.body)}
      </Iffy>
    </DndContext>
  )
}

DNARearranger.displayName = 'DNARearranger'
SortableItem.displayName = 'DNARearrangerItem'
DNARearranger.SortableItem = SortableItem

export default DNARearranger
